// Stock transfer (legacy transfers/StockFormComponent) and stock adjustment
// (legacy stock_adjustments/AdjustmentFormComponent + EditRowItemComponent)
// editors. Both are draft-only from React: transfers are saved as `pending`
// and adjustments as `draft=1`; the stock only moves through the explicit
// Finalize action.
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Edit2, Trash } from 'iconsax-reactjs';

import { AttachmentsSection } from '../../crud/CrudResourcePage';
import { adminApi, apiErrorMessage, apiValidationErrors, createRecord, fetchRecord, fetchSelectOptions, updateRecord, type SelectOption } from '../../api/client';
import type { CrudCustomFormProps, CrudRecord } from '../../crud/types';
import { t, tLabel } from '../../i18n';
import { ensureLocation, useSessionLocation } from '../../session/location';
import {
  CustomFieldsEditor,
  EditorSection,
  asArray,
  asRecord,
  fieldError,
  metaLabel,
  numOr0,
  rowKey,
  useCustomFieldDefs,
  useDebounced,
  useLocations,
  validateCustomFields,
  type ValidationErrors
} from './shared';

export type StockDocKind = 'transfer' | 'adjustment';

type VariationChoice = { id: string; label: string; available: number | null; quantity: number | ''; cost: number | '' };
type SerialChoice = { id: string; number: string };

export type StockLine = {
  key: string;
  id?: string;
  item_id: string;
  code: string;
  name: string;
  quantity: number | '';
  comment: string;
  // adjustment-only
  cost: number | '';
  discount: string;
  batch_no: string;
  expiry_date: string;
  unit_id: string;
  item_unit_id: string;
  units: SelectOption[];
  taxes: string[];
  tax_included: boolean;
  // shared
  hasVariants: boolean;
  variations: VariationChoice[];
  hasSerials: boolean;
  serialOptions: SerialChoice[];
  serials: string[];
};

export type StockDocForm = {
  id?: string;
  date: string;
  reference: string;
  details: string;
  // transfer
  from: string;
  to: string;
  status: string;
  // adjustment
  type: string;
  draft: number;
  extra_attributes: Record<string, unknown>;
  items: StockLine[];
  attachments: File[];
};

const today = () => new Date().toISOString().slice(0, 10);
const str = (value: unknown) => (value == null ? '' : String(value));

function emptyDoc(prefill?: Record<string, unknown>): StockDocForm {
  return {
    date: today(),
    reference: '',
    details: '',
    from: str(prefill?.from),
    to: str(prefill?.to),
    status: 'pending',
    type: str(prefill?.type) || 'addition',
    draft: 1,
    extra_attributes: {},
    items: [],
    attachments: []
  };
}

function unitOptions(item: Record<string, unknown>): SelectOption[] {
  const unit = asRecord(item.unit);
  if (!unit.id) return [];
  return [{ value: str(unit.id), label: str(unit.name) }, ...asArray(unit.subunits).map((sub) => ({ value: str(sub.id), label: str(sub.name) }))];
}

function variationChoices(item: Record<string, unknown>, selected: Array<Record<string, unknown>> = []): VariationChoice[] {
  return asArray(item.variations).map((variation) => {
    const stock = asArray(variation.stock)[0] ?? asArray(variation.location_stock)[0];
    const chosen = selected.find((entry) => str(entry.id) === str(variation.id));
    const pivot = asRecord(chosen?.pivot);
    return {
      id: str(variation.id),
      label: metaLabel(variation.meta) || str(variation.code ?? variation.sku),
      available: stock ? numOr0(stock.quantity) : null,
      quantity: chosen ? numOr0(pivot.quantity ?? chosen.quantity) : '',
      cost: chosen ? numOr0(pivot.cost ?? chosen.cost) : ''
    };
  });
}

/** A new line from an item search/resolve result. */
export function lineFromItem(item: Record<string, unknown>, kind: StockDocKind): StockLine {
  const locationStock = asArray(item.location_stock)[0];
  const variations = variationChoices(item);
  const hasVariants = Boolean(Number(item.has_variants)) && variations.length > 0;
  return {
    key: rowKey('line'),
    item_id: str(item.id),
    code: str(item.code),
    name: str(item.name),
    quantity: 1,
    comment: '',
    cost: kind === 'adjustment' ? numOr0(locationStock?.cost ?? item.cost) : '',
    discount: '',
    batch_no: '',
    expiry_date: '',
    unit_id: str(asRecord(item.unit).id ?? item.unit_id),
    item_unit_id: str(asRecord(item.unit).id ?? item.unit_id),
    units: unitOptions(item),
    taxes: asArray(item.taxes).map((tax) => str(tax.id)),
    tax_included: Boolean(Number(item.tax_included)),
    hasVariants,
    variations,
    hasSerials: Boolean(Number(item.has_serials)) || asArray(item.serials).length > 0,
    serialOptions: asArray(item.serials)
      .filter((serial) => !Number(serial.sold))
      .map((serial) => ({ id: str(serial.id), number: str(serial.number) })),
    serials: []
  };
}

function lineQuantity(line: StockLine) {
  return line.hasVariants ? line.variations.reduce((sum, variation) => sum + numOr0(variation.quantity), 0) : numOr0(line.quantity);
}

/** Editor state from a loaded record (GET transfers/stock/{id} or adjustments/{id}). */
export function docFromRecord(record: Record<string, unknown>, kind: StockDocKind): StockDocForm {
  const items = asArray(record.items).map((row) => {
    const item = asRecord(row.item);
    const variations = variationChoices(item, asArray(row.variations));
    const taxIds = Array.isArray(row.item_taxes) ? row.item_taxes.map(String) : typeof row.item_taxes === 'string' ? safeIds(row.item_taxes) : asArray(row.taxes).map((tax) => str(tax.id));
    const line: StockLine = {
      key: rowKey('line'),
      id: str(row.id),
      item_id: str(row.item_id ?? item.id),
      code: str(row.code ?? item.code),
      name: str(row.name ?? item.name),
      quantity: numOr0(row.quantity),
      comment: str(row.comment),
      cost: kind === 'adjustment' ? numOr0(row.cost) : '',
      discount: str(row.discount === '0' ? '' : row.discount),
      batch_no: str(row.batch_no),
      expiry_date: str(row.expiry_date).slice(0, 10),
      unit_id: str(row.unit_id ?? asRecord(item.unit).id),
      item_unit_id: str(asRecord(item.unit).id ?? item.unit_id),
      units: unitOptions(item),
      taxes: taxIds,
      tax_included: Boolean(Number(item.tax_included)),
      hasVariants: asArray(row.variations).length > 0 || (Boolean(Number(item.has_variants)) && variations.length > 0),
      variations,
      hasSerials: Boolean(Number(item.has_serials)) || asArray(item.serials).length > 0,
      serialOptions: asArray(item.serials).map((serial) => ({ id: str(serial.id), number: str(serial.number) })),
      serials: asArray(row.serials).map((serial) => str(serial.id))
    };
    return line;
  });
  return {
    id: str(record.id),
    date: str(record.date).slice(0, 10) || today(),
    reference: str(record.reference),
    details: str(record.details),
    from: str(record.from ?? asRecord(record.from_location).id),
    to: str(record.to ?? asRecord(record.to_location).id),
    status: str(record.status) || 'pending',
    type: str(record.type) || 'addition',
    draft: record.draft == null ? 1 : Number(record.draft),
    extra_attributes: asRecord(record.extra_attributes),
    items,
    attachments: []
  };
}

function safeIds(value: string) {
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return value ? value.split(',') : [];
  }
}

export function buildTransferPayload(form: StockDocForm, status = 'pending'): Record<string, unknown> {
  return {
    date: form.date || today(),
    reference: form.reference || null,
    details: form.details || null,
    from: form.from,
    to: form.to,
    status,
    items: form.items
      .map((line) => ({
        ...(line.id ? { id: line.id } : {}),
        item_id: line.item_id,
        code: line.code,
        name: line.name,
        quantity: lineQuantity(line),
        comment: line.comment || null,
        // StockTransfer::flatVariations merges [{ variation_id: { quantity } }] into one map.
        variations: line.hasVariants ? line.variations.filter((variation) => numOr0(variation.quantity) > 0).map((variation) => ({ [variation.id]: { quantity: numOr0(variation.quantity) } })) : [],
        serials: line.serials
      }))
      .filter((line) => line.item_id && line.quantity > 0),
    ...(form.attachments.length ? { attachments: form.attachments } : {})
  };
}

export function buildAdjustmentPayload(form: StockDocForm, draft = 1): Record<string, unknown> {
  return {
    type: form.type || 'addition',
    date: form.date || today(),
    reference: form.reference || null,
    details: form.details || null,
    // HARD GUARDRAIL: editor saves are drafts; only Finalize passes draft=0.
    draft: draft === 0 ? 0 : 1,
    extra_attributes: form.extra_attributes,
    items: form.items
      .map((line) => ({
        ...(line.id ? { id: line.id } : {}),
        item_id: line.item_id,
        code: line.code,
        name: line.name,
        quantity: lineQuantity(line),
        cost: numOr0(line.cost),
        discount: line.discount || null,
        batch_no: line.batch_no || null,
        expiry_date: line.expiry_date || null,
        unit_id: line.unit_id || null,
        // OrderDataService compares unit_id with item_unit_id (must be present).
        item_unit_id: line.item_unit_id || null,
        taxes: line.taxes,
        allTaxes: line.taxes,
        tax_included: line.tax_included ? 1 : 0,
        comment: line.comment || null,
        selected: {
          variations: line.hasVariants ? line.variations.filter((variation) => numOr0(variation.quantity) > 0).map((variation) => ({ id: variation.id, quantity: numOr0(variation.quantity), ...(variation.cost !== '' ? { cost: numOr0(variation.cost) } : {}) })) : [],
          serials: line.serials
        }
      }))
      .filter((line) => line.item_id && line.quantity > 0),
    ...(form.attachments.length ? { attachments: form.attachments } : {})
  };
}

/** Finalize path (engine PUTs the loaded record back through normalizeBeforeSave). */
export function normalizeTransferRecordForSave(payload: Record<string, unknown>) {
  return buildTransferPayload(docFromRecord(payload, 'transfer'), payload.status === 'transferred' ? 'transferred' : 'pending');
}

export function normalizeAdjustmentRecordForSave(payload: Record<string, unknown>) {
  return buildAdjustmentPayload(docFromRecord(payload, 'adjustment'), payload.draft === 0 ? 0 : 1);
}

export function makeStockDocEditor(kind: StockDocKind) {
  return function StockDocEditorForKind(props: CrudCustomFormProps) {
    return <StockDocumentEditor {...props} kind={kind} />;
  };
}

function StockDocumentEditor({ recordId, prefill, onClose, onSaved, kind }: CrudCustomFormProps & { kind: StockDocKind }) {
  const resource = kind === 'transfer' ? 'transfers/stock' : 'adjustments';
  const editing = recordId != null;
  const [form, setForm] = useState<StockDocForm>(() => emptyDoc(prefill));
  const [record, setRecord] = useState<CrudRecord | null>(null);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [taxes, setTaxes] = useState<SelectOption[]>([]);
  const locations = useLocations();
  const session = useSessionLocation();
  const customDefs = useCustomFieldDefs(kind === 'adjustment' ? 'adjustments' : null);

  const load = async () => {
    if (!editing) return;
    setLoading(true);
    try {
      if (kind === 'adjustment') await ensureLocation();
      const loaded = await fetchRecord<CrudRecord>(resource, recordId!);
      setRecord(loaded);
      setForm(docFromRecord(loaded, kind));
    } catch (err) {
      setNotice(apiErrorMessage(err, t('common.unableToLoad', { name: '' })));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    if (kind === 'adjustment') fetchSelectOptions('taxes/search').then(setTaxes).catch(() => setTaxes([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordId]);

  useEffect(() => {
    if (kind === 'transfer' && !form.from && !editing && session.currentId) setForm((current) => (current.from ? current : { ...current, from: session.currentId! }));
  }, [kind, editing, session.currentId, form.from]);

  const locked = editing && (kind === 'transfer' ? form.status !== 'pending' : form.draft === 0);

  const addItem = (item: Record<string, unknown>) => {
    setForm((current) => {
      const line = lineFromItem(item, kind);
      const existing = current.items.findIndex((entry) => entry.item_id === line.item_id);
      if (existing >= 0 && !line.hasVariants && !line.hasSerials) {
        return { ...current, items: current.items.map((entry, index) => (index === existing ? { ...entry, quantity: numOr0(entry.quantity) + 1 } : entry)) };
      }
      const items = [...current.items, line];
      if (line.hasVariants || line.hasSerials) window.setTimeout(() => setEditIndex(items.length - 1), 0);
      return { ...current, items };
    });
  };

  const validate = () => {
    const next: ValidationErrors = {};
    if (!form.date) next.date = [t('catalog.validation.required', { field: tLabel('Date') })];
    if (kind === 'transfer') {
      if (!form.from) next.from = [t('catalog.validation.required', { field: t('catalog.transfer.from') })];
      if (!form.to) next.to = [t('catalog.validation.required', { field: t('catalog.transfer.to') })];
      if (form.from && form.from === form.to) next.to = [t('catalog.transfer.sameLocation')];
    }
    if (!form.items.some((line) => lineQuantity(line) > 0)) next.items = [t('catalog.validation.linesRequired')];
    form.items.forEach((line, index) => {
      if (line.hasSerials && line.serials.length && line.serials.length !== lineQuantity(line)) next[`items.${index}.serials`] = [t('catalog.validation.serialsMismatch', { name: line.name })];
    });
    Object.assign(next, validateCustomFields(customDefs, form.extra_attributes));
    return next;
  };

  const save = async () => {
    const validation = validate();
    setErrors(validation);
    if (Object.keys(validation).length) {
      setNotice(Object.values(validation).flat()[0] ?? t('common.fixHighlighted'));
      return;
    }
    setSaving(true);
    setNotice(null);
    try {
      if (kind === 'adjustment') await ensureLocation();
      const payload = kind === 'transfer' ? buildTransferPayload(form) : buildAdjustmentPayload(form);
      const response = editing ? await updateRecord<CrudRecord>(resource, recordId!, payload) : await createRecord<CrudRecord>(resource, payload);
      if (response && response.success === false) {
        setNotice(response.message ?? t('common.saveFailed'));
        return;
      }
      onSaved(response?.data);
    } catch (err) {
      const validationErrors = apiValidationErrors(err);
      setErrors(validationErrors);
      setNotice(Object.keys(validationErrors).length ? Object.values(validationErrors).flat().slice(0, 3).join(' ') : apiErrorMessage(err, t('common.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const title = kind === 'transfer' ? (editing ? t('catalog.transfer.editTitle') : t('catalog.transfer.addTitle')) : editing ? t('catalog.adjustment.editTitle') : t('catalog.adjustment.addTitle');
  const editLine = editIndex != null ? form.items[editIndex] : null;

  return (
    <Dialog open onClose={() => !saving && onClose()} fullWidth maxWidth="lg">
      <DialogTitle><Typography component="span" variant="h5" fontWeight={700}>{title}</Typography></DialogTitle>
      <DialogContent dividers>
        {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}
        {locked ? <Alert severity="warning" sx={{ mb: 2 }}>{kind === 'transfer' ? t('catalog.transfer.locked') : t('catalog.adjustment.locked')}</Alert> : null}
        {notice ? <Alert severity="error" sx={{ mb: 2 }}>{notice}</Alert> : null}
        <Stack spacing={2}>
          <EditorSection title={t('catalog.section.details')} description={kind === 'transfer' ? t('catalog.transfer.pendingInfo') : t('catalog.adjustment.draftInfo')}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
              <TextField size="small" type="date" label={tLabel('Date')} required InputLabelProps={{ shrink: true }} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} error={Boolean(fieldError(errors, 'date'))} helperText={fieldError(errors, 'date')} />
              <TextField size="small" label={tLabel('Reference')} value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} helperText={fieldError(errors, 'reference') || t('catalog.referenceTip')} error={Boolean(fieldError(errors, 'reference'))} />
              {kind === 'adjustment' ? (
                <TextField select size="small" label={tLabel('Type')} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  {['addition', 'subtraction', 'damage'].map((value) => <MenuItem key={value} value={value}>{t(`catalog.adjustment.type.${value}`)}</MenuItem>)}
                </TextField>
              ) : (
                <Box />
              )}
              {kind === 'transfer' ? (
                <>
                  <TextField select size="small" label={t('catalog.transfer.from')} required value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} error={Boolean(fieldError(errors, 'from'))} helperText={fieldError(errors, 'from')} disabled={form.items.length > 0}>
                    {locations.map((location) => <MenuItem key={location.value} value={location.value}>{location.label}</MenuItem>)}
                  </TextField>
                  <TextField select size="small" label={t('catalog.transfer.to')} required value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} error={Boolean(fieldError(errors, 'to'))} helperText={fieldError(errors, 'to')}>
                    {locations.map((location) => <MenuItem key={location.value} value={location.value}>{location.label}</MenuItem>)}
                  </TextField>
                </>
              ) : null}
            </Box>
          </EditorSection>

          <EditorSection title={t('catalog.lines')}>
            <Stack spacing={1.5}>
              <ItemSearchBar kind={kind} from={form.from} onPick={addItem} disabled={locked || (kind === 'transfer' && !form.from)} />
              {fieldError(errors, 'items') ? <Alert severity="error">{fieldError(errors, 'items')}</Alert> : null}
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{tLabel('Item')}</TableCell>
                      <TableCell width={130}>{tLabel('Quantity')}</TableCell>
                      {kind === 'adjustment' ? <TableCell width={130}>{tLabel('Cost')}</TableCell> : null}
                      <TableCell>{t('catalog.lineDetails')}</TableCell>
                      <TableCell width={90} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!form.items.length ? (
                      <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">{t('catalog.noLines')}</Typography></TableCell></TableRow>
                    ) : null}
                    {form.items.map((line, index) => (
                      <TableRow key={line.key}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>{line.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{line.code}</Typography>
                          {fieldError(errors, `items.${index}.serials`) ? <Typography variant="caption" color="error" display="block">{fieldError(errors, `items.${index}.serials`)}</Typography> : null}
                        </TableCell>
                        <TableCell>
                          {line.hasVariants ? (
                            <Typography variant="body2">{lineQuantity(line)}</Typography>
                          ) : (
                            <TextField size="small" type="number" inputProps={{ min: 0, step: 'any' }} value={line.quantity} disabled={locked} onChange={(e) => setForm({ ...form, items: form.items.map((entry, i) => (i === index ? { ...entry, quantity: e.target.value === '' ? '' : Number(e.target.value) } : entry)) })} />
                          )}
                        </TableCell>
                        {kind === 'adjustment' ? (
                          <TableCell>
                            <TextField size="small" type="number" inputProps={{ min: 0, step: 'any' }} value={line.cost} disabled={locked} onChange={(e) => setForm({ ...form, items: form.items.map((entry, i) => (i === index ? { ...entry, cost: e.target.value === '' ? '' : Number(e.target.value) } : entry)) })} />
                          </TableCell>
                        ) : null}
                        <TableCell>
                          <Stack direction="row" flexWrap="wrap" gap={0.5}>
                            {line.variations.filter((variation) => numOr0(variation.quantity) > 0).map((variation) => <Chip key={variation.id} size="small" label={`${variation.label} × ${variation.quantity}`} />)}
                            {line.serials.length ? <Chip size="small" color="info" label={t('catalog.serialsCount', { count: line.serials.length })} /> : null}
                            {line.batch_no ? <Chip size="small" label={`${t('catalog.batchNo')}: ${line.batch_no}`} /> : null}
                            {line.expiry_date ? <Chip size="small" label={`${t('catalog.expiryDate')}: ${line.expiry_date}`} /> : null}
                            {line.discount ? <Chip size="small" label={`${tLabel('Discount')}: ${line.discount}`} /> : null}
                            {line.comment ? <Chip size="small" variant="outlined" label={line.comment} /> : null}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={t('action.edit')}>
                            <span><IconButton size="small" disabled={locked} onClick={() => setEditIndex(index)}><Edit2 size={16} /></IconButton></span>
                          </Tooltip>
                          <Tooltip title={t('action.delete')}>
                            <span><IconButton size="small" color="error" disabled={locked} onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== index) })}><Trash size={16} /></IconButton></span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Stack>
          </EditorSection>

          {kind === 'adjustment' ? (
            <CustomFieldsEditor defs={customDefs} values={form.extra_attributes} errors={errors} onChange={(slug, value) => setForm({ ...form, extra_attributes: { ...form.extra_attributes, [slug]: value } })} />
          ) : null}

          <EditorSection title={t('crud.attachments')}>
            <Stack spacing={1.5}>
              {record ? (
                <AttachmentsSection record={record} canDelete={!locked} onDeleted={() => void load()} onError={(message) => setNotice(message)} />
              ) : null}
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Button variant="outlined" component="label" disabled={locked}>
                  {t('action.chooseFiles')}
                  <input hidden multiple type="file" accept=".jpg,.jpeg,.png,.pdf,.zip" onChange={(e) => setForm({ ...form, attachments: [...form.attachments, ...Array.from(e.target.files ?? [])] })} />
                </Button>
                {form.attachments.map((file, index) => <Chip key={`${file.name}-${index}`} label={file.name} onDelete={() => setForm({ ...form, attachments: form.attachments.filter((_, i) => i !== index) })} />)}
              </Stack>
              {fieldError(errors, 'attachments.0') ? <Typography variant="caption" color="error">{fieldError(errors, 'attachments.0')}</Typography> : null}
            </Stack>
          </EditorSection>

          <TextField size="small" label={tLabel('Details')} multiline minRows={2} value={form.details} disabled={locked} onChange={(e) => setForm({ ...form, details: e.target.value })} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button color="inherit" onClick={onClose} disabled={saving}>{t('action.cancel')}</Button>
        <Button variant="contained" onClick={() => void save()} disabled={saving || loading || locked}>{saving ? t('action.saving') : t('action.save')}</Button>
      </DialogActions>
      {editLine ? (
        <LineDialog
          kind={kind}
          line={editLine}
          taxes={taxes}
          onClose={() => setEditIndex(null)}
          onSave={(line) => {
            setForm((current) => ({ ...current, items: current.items.map((entry, i) => (i === editIndex ? line : entry)) }));
            setEditIndex(null);
          }}
        />
      ) : null}
    </Dialog>
  );
}

function ItemSearchBar({ kind, from, onPick, disabled }: { kind: StockDocKind; from: string; onPick: (item: Record<string, unknown>) => void; disabled?: boolean }) {
  const [input, setInput] = useState('');
  const query = useDebounced(input, 300);
  const [results, setResults] = useState<Array<Record<string, unknown>>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resolve = async (item: Record<string, unknown>) => {
    if (kind === 'transfer') return item;
    // Adjustments: resolve the line like the legacy Order mixin (purchase_order_items/{id}).
    const response = await adminApi.get(`purchase_order_items/${item.id ?? item.value}`);
    const resolved = response.data as Record<string, unknown>;
    if (Number(resolved.has_serials)) {
      const full = await adminApi.get(`items/${resolved.id}`).then((r) => r.data as Record<string, unknown>).catch(() => null);
      if (full) resolved.serials = full.serials;
    }
    return resolved;
  };

  const pick = async (item: Record<string, unknown>) => {
    try {
      setLoading(true);
      onPick(await resolve(item));
      setInput('');
      setResults([]);
    } catch (err) {
      setError(apiErrorMessage(err, t('catalog.searchFailed')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!query.trim() || query.includes('(')) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        await ensureLocation();
        const response = kind === 'transfer'
          ? await adminApi.get('items_wt/search', { params: { q: query, location: from } })
          : await adminApi.get('items_wp/search', { params: { type: 'standard,service', q: query } });
        const rows = Array.isArray(response.data) ? (response.data as Array<Record<string, unknown>>) : [];
        if (cancelled) return;
        if (rows.length === 1) {
          // Legacy behaviour: a single match (e.g. a scanned barcode) is added directly.
          await pick(rows[0]);
          return;
        }
        setResults(rows);
      } catch (err) {
        if (!cancelled) setError(apiErrorMessage(err, t('catalog.searchFailed')));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, kind, from]);

  return (
    <Stack spacing={0.5}>
      <Autocomplete
        size="small"
        options={results}
        disabled={disabled}
        inputValue={input}
        value={null}
        filterOptions={(items) => items}
        loading={loading}
        onInputChange={(_event, value, reason) => {
          if (reason === 'input') setInput(value);
        }}
        onChange={(_event, value) => {
          if (value) void pick(value as Record<string, unknown>);
        }}
        getOptionLabel={(option) => str((option as Record<string, unknown>).label ?? `${(option as Record<string, unknown>).name} (${(option as Record<string, unknown>).code})`)}
        noOptionsText={input ? t('common.noResults') : t('catalog.typeToSearch')}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={kind === 'transfer' && !from ? t('catalog.transfer.pickFromFirst') : t('catalog.scanOrSearch')}
            InputProps={{ ...params.InputProps, endAdornment: <>{loading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</> }}
          />
        )}
      />
      {error ? <Typography variant="caption" color="error">{error}</Typography> : null}
    </Stack>
  );
}

function LineDialog({ kind, line, taxes, onClose, onSave }: { kind: StockDocKind; line: StockLine; taxes: SelectOption[]; onClose: () => void; onSave: (line: StockLine) => void }) {
  const [draft, setDraft] = useState<StockLine>(line);
  const [error, setError] = useState<string | null>(null);
  const quantity = lineQuantity(draft);
  const serialOptions = useMemo(() => {
    const selected = new Set(draft.serials);
    return draft.serialOptions.filter((option) => option.number).map((option) => ({ ...option, selected: selected.has(option.id) }));
  }, [draft.serialOptions, draft.serials]);

  const submit = () => {
    if (!(quantity > 0)) return setError(t('catalog.validation.quantityPositive'));
    if (draft.hasSerials && draft.serials.length && draft.serials.length !== quantity) return setError(t('catalog.validation.serialsMismatch', { name: draft.name }));
    onSave(draft);
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{draft.name} <Typography component="span" variant="caption" color="text.secondary">({draft.code})</Typography></DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            {!draft.hasVariants ? (
              <TextField size="small" type="number" label={tLabel('Quantity')} value={draft.quantity} onChange={(e) => setDraft({ ...draft, quantity: e.target.value === '' ? '' : Number(e.target.value) })} />
            ) : (
              <TextField size="small" label={tLabel('Quantity')} value={quantity} InputProps={{ readOnly: true }} helperText={t('catalog.quantityFromVariations')} />
            )}
            {kind === 'adjustment' ? (
              <>
                {draft.units.length > 1 ? (
                  <TextField select size="small" label={tLabel('Unit')} value={draft.unit_id} onChange={(e) => setDraft({ ...draft, unit_id: e.target.value })}>
                    {draft.units.map((unit) => <MenuItem key={String(unit.value)} value={String(unit.value)}>{unit.label}</MenuItem>)}
                  </TextField>
                ) : <Box />}
                <TextField size="small" type="number" label={tLabel('Cost')} value={draft.cost} onChange={(e) => setDraft({ ...draft, cost: e.target.value === '' ? '' : Number(e.target.value) })} />
                <TextField size="small" label={tLabel('Discount')} value={draft.discount} onChange={(e) => setDraft({ ...draft, discount: e.target.value })} helperText={t('catalog.discountTip')} />
                <TextField size="small" label={t('catalog.batchNo')} value={draft.batch_no} onChange={(e) => setDraft({ ...draft, batch_no: e.target.value })} />
                <TextField size="small" type="date" label={t('catalog.expiryDate')} InputLabelProps={{ shrink: true }} inputProps={{ min: new Date().toISOString().slice(0, 10) }} value={draft.expiry_date} onChange={(e) => setDraft({ ...draft, expiry_date: e.target.value })} />
                <TextField
                  select
                  size="small"
                  label={t('nav.taxes')}
                  value={draft.taxes}
                  SelectProps={{ multiple: true, renderValue: (selected) => (selected as string[]).map((id) => taxes.find((tax) => String(tax.value) === id)?.label ?? id).join(', ') }}
                  onChange={(e) => setDraft({ ...draft, taxes: typeof e.target.value === 'string' ? e.target.value.split(',') : (e.target.value as string[]) })}
                >
                  {taxes.map((tax) => (
                    <MenuItem key={String(tax.value)} value={String(tax.value)}><Checkbox size="small" checked={draft.taxes.includes(String(tax.value))} />{tax.label}</MenuItem>
                  ))}
                </TextField>
                <FormControlLabel control={<Checkbox checked={draft.tax_included} onChange={(e) => setDraft({ ...draft, tax_included: e.target.checked })} />} label={t('catalog.taxIncluded')} />
              </>
            ) : null}
          </Box>
          {draft.hasVariants ? (
            <EditorSection title={t('catalog.variations')}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('catalog.variation')}</TableCell>
                    <TableCell>{t('catalog.available')}</TableCell>
                    <TableCell width={140}>{tLabel('Quantity')}</TableCell>
                    {kind === 'adjustment' ? <TableCell width={140}>{tLabel('Cost')}</TableCell> : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {draft.variations.map((variation, index) => (
                    <TableRow key={variation.id}>
                      <TableCell>{variation.label}</TableCell>
                      <TableCell>{variation.available ?? '—'}</TableCell>
                      <TableCell>
                        <TextField size="small" type="number" inputProps={{ min: 0, step: 'any' }} value={variation.quantity} onChange={(e) => setDraft({ ...draft, variations: draft.variations.map((entry, i) => (i === index ? { ...entry, quantity: e.target.value === '' ? '' : Number(e.target.value) } : entry)) })} />
                      </TableCell>
                      {kind === 'adjustment' ? (
                        <TableCell>
                          <TextField size="small" type="number" inputProps={{ min: 0, step: 'any' }} value={variation.cost} placeholder={String(draft.cost)} onChange={(e) => setDraft({ ...draft, variations: draft.variations.map((entry, i) => (i === index ? { ...entry, cost: e.target.value === '' ? '' : Number(e.target.value) } : entry)) })} />
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </EditorSection>
          ) : null}
          {draft.hasSerials ? (
            <EditorSection title={t('catalog.serialNumbers')} description={t('catalog.serialsSelected', { count: draft.serials.length, total: quantity })}>
              {serialOptions.length ? (
                <Autocomplete
                  multiple
                  size="small"
                  options={serialOptions}
                  value={serialOptions.filter((option) => option.selected)}
                  onChange={(_event, value) => setDraft({ ...draft, serials: value.map((option) => option.id) })}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(option) => option.number}
                  renderInput={(params) => <TextField {...params} placeholder={t('catalog.selectSerials')} />}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">{t('catalog.noSerialsAvailable')}</Typography>
              )}
            </EditorSection>
          ) : null}
          <TextField size="small" label={t('catalog.comment')} multiline minRows={2} value={draft.comment} onChange={(e) => setDraft({ ...draft, comment: e.target.value })} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('action.cancel')}</Button>
        <Button variant="contained" onClick={submit}>{t('action.apply')}</Button>
      </DialogActions>
    </Dialog>
  );
}
